// /opt/isp_erp/frontend/src/components/warehouse/ResumenDiario.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { PictureAsPdf, Refresh, ExpandMore } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import { cargarContextoBodega } from '../../utils/contextoBodega';

const hoyLocal = () => {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

const fmt = (n) => {
  const x = Number(n || 0);
  return Number.isInteger(x) ? String(x) : x.toFixed(2);
};

const Tabla = ({ columnas, filas, vacio }) =>
  filas.length === 0 ? (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      {vacio}
    </Typography>
  ) : (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            {columnas.map((c) => (
              <TableCell key={c.key} align={c.align || 'left'}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((f, i) => (
            <TableRow key={i} hover>
              {columnas.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'}>
                  {c.render ? c.render(f) : f[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

const colsMateriales = [
  { key: 'codigo', label: 'Código' },
  { key: 'nombre', label: 'Material' },
  { key: 'unidad', label: 'Unidad' },
  { key: 'usado', label: 'Usado', align: 'right', render: (f) => <strong>{fmt(f.usado)}</strong> },
  { key: 'recuperado', label: 'Recuperado en retiros', align: 'right', render: (f) => fmt(f.recuperado) },
  { key: 'neto', label: 'Neto consumido', align: 'right', render: (f) => fmt(f.neto) }
];

const colsInstalados = [
  { key: 'sn', label: 'SN', render: (f) => <strong>{f.sn}</strong> },
  { key: 'modelo', label: 'Modelo' },
  { key: 'cliente', label: 'Cliente' },
  { key: 'ticket', label: 'Ticket' },
  { key: 'hora', label: 'Hora' }
];

const colsRetirados = [...colsInstalados, { key: 'motivo', label: 'Motivo' }];

const colsModelos = [
  { key: 'modelo', label: 'Modelo' },
  { key: 'instalados', label: 'Instalados', align: 'right' },
  { key: 'retirados', label: 'Retirados', align: 'right' }
];

const ResumenDiario = () => {
  const [fecha, setFecha] = useState(hoyLocal());
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [ctx, setCtx] = useState(null);
  const [ubicacion, setUbicacion] = useState('');

  useEffect(() => {
    cargarContextoBodega().then((c) => setCtx(c || { central: true, sucursales: [] }));
  }, []);

  const cargar = useCallback(async () => {
    if (!fecha) return;
    setLoading(true);
    setError('');
    try {
      const response = await warehouseAPI.getResumenDiario(ubicacion ? { fecha, ubicacion } : { fecha });
      setDatos(response.data);
    } catch (err) {
      setDatos(null);
      const detalle = err.response?.data?.detail;
      setError(typeof detalle === 'string' ? detalle : 'Error al cargar el resumen diario');
    } finally {
      setLoading(false);
    }
  }, [fecha, ubicacion]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const descargarPDF = async () => {
    setDescargando(true);
    try {
      const response = await warehouseAPI.descargarResumenDiarioPDF(ubicacion ? { fecha, ubicacion } : { fecha });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resumen_diario_${fecha}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando el PDF:', err);
      setError('No se pudo generar el PDF del resumen diario');
    } finally {
      setDescargando(false);
    }
  };

  const total = datos?.total;

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Resumen diario de materiales y equipos
          </Typography>
          <TextField
            type="date"
            size="small"
            label="Fecha"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {ctx && ctx.central !== false && (ctx.sucursales || []).length > 0 && (
            <TextField select size="small" label="Ubicación" value={ubicacion} SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }} onChange={(e) => setUbicacion(e.target.value)}>
              <option value="">Todas</option>
              <option value="central">Bodega central</option>
              {ctx.sucursales.map((s) => (<option key={s.id} value={String(s.id)}>{s.nombre}</option>))}
            </TextField>
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={cargar} disabled={loading}>
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={descargando ? <CircularProgress size={18} /> : <PictureAsPdf />}
            onClick={descargarPDF}
            disabled={descargando || !fecha}
          >
            Descargar PDF
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && datos && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={`Técnicos con actividad: ${total.tecnicos_activos}`} />
              <Chip color="info" label={`Equipos instalados: ${total.equipos_instalados}`} />
              <Chip color="warning" label={`Equipos retirados: ${total.equipos_retirados}`} />
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Resumen total del día
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Materiales utilizados (todos los técnicos)
            </Typography>
            <Tabla columnas={colsMateriales} filas={total.materiales} vacio="Sin consumo de materiales en esta fecha." />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Equipos por modelo
            </Typography>
            <Tabla columnas={colsModelos} filas={total.equipos_por_modelo} vacio="Sin movimientos de equipos en esta fecha." />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }} gutterBottom>
              Detalle por técnico ({datos.tecnicos.length}) — haz clic en uno para ver su desglose
            </Typography>
            {datos.tecnicos.length > 6 && (
              <TextField
                size="small"
                label="Buscar técnico"
                value={filtroTecnico}
                onChange={(e) => setFiltroTecnico(e.target.value)}
                sx={{ mb: 2, minWidth: 260 }}
              />
            )}
            {datos.tecnicos
              .filter((t) => t.tecnico.toLowerCase().includes(filtroTecnico.trim().toLowerCase()))
              .map((t) => (
                <Accordion
                  key={t.tecnico_id ?? 'sin-tecnico'}
                  variant="outlined"
                  disableGutters
                  TransitionProps={{ unmountOnExit: true }}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
                      <Typography fontWeight="bold" sx={{ minWidth: 160 }}>
                        {t.tecnico}
                      </Typography>
                      <Chip size="small" color="info" label={`Instalados: ${t.equipos_instalados.length}`} />
                      <Chip size="small" color="warning" label={`Retirados: ${t.equipos_retirados.length}`} />
                      <Chip size="small" label={`Materiales: ${t.materiales.length}`} />
                      <Chip size="small" variant="outlined" label={`Sucursal: ${t.sucursal || '—'}`} />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Materiales
                    </Typography>
                    <Tabla columnas={colsMateriales} filas={t.materiales} vacio="Sin materiales." />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Equipos instalados ({t.equipos_instalados.length})
                    </Typography>
                    <Tabla columnas={colsInstalados} filas={t.equipos_instalados} vacio="Sin equipos instalados." />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Equipos retirados ({t.equipos_retirados.length})
                    </Typography>
                    <Tabla columnas={colsRetirados} filas={t.equipos_retirados} vacio="Sin equipos retirados." />
                  </AccordionDetails>
                </Accordion>
              ))}

            {datos.tecnicos.length === 0 && (
              <Alert severity="info">No hubo instalaciones, retiros ni consumo de materiales en esta fecha.</Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ResumenDiario;
